/**
 * Alcohol band boundaries for the taste profile plot.
 *
 * These mirror the backend's canonical definition — `WINE_ALCOHOL_PERCENTAGE_LOW_LIMIT`
 * and `WINE_ALCOHOL_PERCENTAGE_HIGH_LIMIT` in the API's settings, applied by
 * `Wine.get_wine_alcohol_percentage_level()`. Under 12% is low, 12% up to but not
 * including 14% is medium, 14% and above is high.
 *
 * The boundaries have to stay in step with the backend because the same wine is
 * banded twice on one screen: the taste summary text ("high alcohol") comes from the
 * API, while the marker on this plot is banded here. They also have to match the band
 * labels in `myTasteProfile:alcohol.*`, which read "under 12%" / "12-14%" / "14%+".
 */
export const WINE_ALCOHOL_PERCENTAGE_LOW_LIMIT = 12;
export const WINE_ALCOHOL_PERCENTAGE_HIGH_LIMIT = 14;

/** Position along the 0-1 plot bar that each band maps to. */
export const ALCOHOL_BAND_PLOT_POSITION = {
  LOW: 0,
  MEDIUM: 0.5,
  HIGH: 1,
};

/**
 * Maps a raw ABV to its position on the plot bar. This is used for both the
 * wine's alcohol percentage and the member's average alcohol percentage because
 * the API returns the latter as a raw ABV rather than a normalized 0-1 value.
 *
 * Missing ABVs plot mid-bar rather than being left off — the marker has to sit
 * somewhere, and medium is the least misleading guess.
 */
export const getAlcoholBandPlotPosition = (alcoholPercentage: number | null | undefined): number => {
  if (alcoholPercentage === null || alcoholPercentage === undefined) {
    return ALCOHOL_BAND_PLOT_POSITION.MEDIUM;
  }
  if (alcoholPercentage < WINE_ALCOHOL_PERCENTAGE_LOW_LIMIT) {
    return ALCOHOL_BAND_PLOT_POSITION.LOW;
  }
  if (alcoholPercentage >= WINE_ALCOHOL_PERCENTAGE_HIGH_LIMIT) {
    return ALCOHOL_BAND_PLOT_POSITION.HIGH;
  }
  return ALCOHOL_BAND_PLOT_POSITION.MEDIUM;
};
